/**
 * Client for `GET /api/sheets-check`, plus the log entries the panel renders.
 *
 * Kept apart from the component so the fetch/shape handling is testable without
 * rendering, and so the panel has no knowledge of the wire format beyond this.
 */

/** Mirrors `SheetsCheckResponse` in server/routes/sheets-check.ts. */
export interface SheetsCheckResponse {
  ok: boolean
  ts: string
  access: string
  adapter: string
  sheetId: string | null
  identity: string | null
  impersonating: boolean
  firstTab: string | null
  wrote: boolean
  failure: { step: string; detail: string } | null
}

export interface LogEntry {
  id: string
  at: string
  level: 'ok' | 'warn' | 'fail'
  title: string
  lines: string[]
}

/** `read+write` is the only unambiguous pass; `read-only` is a partial result. */
export function levelFor(res: SheetsCheckResponse): LogEntry['level'] {
  if (!res.ok) return 'fail'
  return res.access === 'read+write' ? 'ok' : 'warn'
}

/** Flatten a response into the lines the panel shows, in operator order. */
export function toLogEntry(res: SheetsCheckResponse, id: string): LogEntry {
  const lines = [
    `access    : ${res.access}`,
    `adapter   : ${res.adapter}`,
    `identity  : ${res.identity ?? '(unresolved)'}${res.impersonating ? ' (impersonated)' : ''}`,
    `sheet     : ${res.sheetId ?? '(unset)'}`,
  ]
  if (res.firstTab) lines.push(`first tab : ${res.firstTab}`)
  if (res.wrote) lines.push('write     : stamp written to A1 and read back')
  if (res.failure) lines.push('', `FAILED at ${res.failure.step}:`, res.failure.detail)

  return {
    id,
    at: new Date(res.ts).toLocaleTimeString(),
    level: levelFor(res),
    title: res.ok
      ? `PASS — ${res.access}`
      : `FAIL — ${res.failure?.step ?? 'unknown'} (${res.access})`,
    lines,
  }
}

/**
 * Run the check. Never throws: a transport failure is itself a result worth
 * logging, and the panel exists precisely to show what went wrong.
 */
export async function runSheetsCheck(
  opts: { write?: boolean } = {},
  fetchImpl: typeof fetch = fetch,
): Promise<SheetsCheckResponse> {
  const url = `/api/sheets-check${opts.write ? '?write=1' : ''}`
  try {
    const res = await fetchImpl(url)
    if (!res.ok) {
      return failureResponse('http', `HTTP ${res.status} from ${url}`)
    }
    return (await res.json()) as SheetsCheckResponse
  } catch (err) {
    return failureResponse(
      'transport',
      `could not reach ${url} — ${err instanceof Error ? err.message : String(err)}. ` +
        'Is the API server running (pnpm dev:server)?',
    )
  }
}

function failureResponse(step: string, detail: string): SheetsCheckResponse {
  return {
    ok: false,
    ts: new Date().toISOString(),
    access: 'none',
    adapter: 'unknown',
    sheetId: null,
    identity: null,
    impersonating: false,
    firstTab: null,
    wrote: false,
    failure: { step, detail },
  }
}
